import React, { useEffect, useRef, useState } from "react";
import { Chart, registerables } from "chart.js";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import McqResultCard from "./McqResultCard";

Chart.register(...registerables);

const DifficultyChart = ({ data, assessmentIsAdaptive, studentResultData }) => {
  const canvasRef = useRef(null);
  let chartInstance = useRef(null);

  const [open, setOpen] = React.useState(false);
  const [selectedPoint, setSelectedPoint] = React.useState(null);

  useEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = canvasRef.current.getContext("2d");
    const totalScore = studentResultData?.assessment?.isAdaptive
      ? Number(studentResultData?.ability ?? 0)
      : Number(studentResultData?.assessment?.total_marks ?? 0);

    const values = (data?.values || [])
      .map((v) => Number(v))
      .filter((v) => !isNaN(v));

    const minY = values.length ? Math.floor(Math.min(0, ...values)) : 0;
    const maxY = Math.ceil(
      Math.max(totalScore, ...(values.length ? values : [0])),
    );

    chartInstance.current = new Chart(ctx, {
      type: "line",
      data: {
        labels: data?.labels,
        datasets: [
          {
            label: assessmentIsAdaptive ? "Ability Score Path" : "Score Path",
            data: values,
            backgroundColor: "#4635B1",
            borderColor: "#4635B1",
            cursor: "pointer",
            borderWidth: 3,
            pointBackgroundColor: data?.isCorrect?.map((status) => {
              if (status === "correct") return "#00b050";
              if (status === "partial") return "#ed7d31";
              return "#c00000";
            }),
            pointRadius: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false, // ⭐ ye add karo
        scales: {
          x: {
            type: "category",
            grid: { color: "rgba(0, 0, 0, 0.5)" },
            ticks: { color: "black" },
          },
          y: {
            type: "linear",
            min: minY,
            max: maxY,
            ticks: {
              stepSize: 1,
              callback: function (value) {
                return value.toFixed(1);
              },
            },
            grid: { color: "rgba(0, 0, 0, 0.4)" },
          },
        },
        onClick: (event, elements) => {
          if (elements.length > 0) {
            const elementIndex = elements[0].index;
            const clickedData = {
              label: data.labels[elementIndex],
              value: values[elementIndex],
              isCorrect: data.isCorrect[elementIndex],
              data: data.data[elementIndex],
              isDescriptive: data.isDescriptive[elementIndex],
              teacherComment: data.teacherComments[elementIndex],
              obtained_marks: data.obtained_marks[elementIndex],
            };
            setSelectedPoint(clickedData);
            setOpen(true);
          }
        },
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data]);

  return (
    <>
      {/* ⭐ Wrapper — chart apne parent ka size lega */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
        }}
      >
        <canvas ref={canvasRef}></canvas>
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          zIndex: 1700, // ⭐ parent modal (1500) se upar
        }}
        PaperProps={{
          sx: { zIndex: 1700 },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, ml: 1, color: "#f97e00" }}>
          Your Response
          <IconButton
            aria-label="close"
            onClick={() => {
              setOpen(false);
              setSelectedPoint(null);
            }}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          className="orange-scrollBar"
          style={{ overflowY: "scroll", height: "600px" }}
        >
          {selectedPoint ? (
            <McqResultCard
              assessmentIsAdaptive={assessmentIsAdaptive}
              value={selectedPoint.value}
              data={selectedPoint.data}
              isCorrect={selectedPoint.isCorrect}
              label={selectedPoint.label}
              isDescriptive={selectedPoint.isDescriptive}
              teacherComment={selectedPoint.teacherComment}
              obtained_marks={selectedPoint.obtained_marks}
            />
          ) : (
            <Typography>No data available</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DifficultyChart;
